import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainTableState, transformTableStateToTRPCInput } from '@/lib/utils/table-state-utils';

interface UseTablePageProps {
  initialSorting: Array<{ id: string; desc: boolean }>;
  filterMapping?: Record<string, string | ((value: any) => any)>;
  queryParamsMapping?: Record<string, (value: URLSearchParams) => any>;
  sortMapping?: Record<string, any>;
  columnIds?: string[] | Set<string>;
}

const getDefaultPageIndex = (searchParams: URLSearchParams) => {
  const maybePage = searchParams.get('page');
  if (maybePage && !Number.isNaN(parseInt(maybePage))) {
    const page = parseInt(maybePage)
    return page > 0 ? page - 1 : 0;
  }
  return 0
}
const getDefaultPageSize = (searchParams: URLSearchParams) => {
  const maybePageSize = searchParams.get('perPage');
  if (maybePageSize && !Number.isNaN(parseInt(maybePageSize))) {
    return parseInt(maybePageSize);
  }
  return 20;
}

const getDefaultFilter = (searchParams: URLSearchParams, queryParamsMapping?: Record<string, (value: URLSearchParams) => any>) => {
  if (!queryParamsMapping) {
    return [];
  }
  const filters = Object.entries(queryParamsMapping).map(([key, value]) => ({ id: key, value: value(searchParams) })).filter(r => r.value !== undefined && r.value !== null);
  return filters;
}

const getDefaultSorting = (searchParams: URLSearchParams, initialSorting: { id: string; desc: boolean }[]) => {
  const sorting = searchParams.get('sort');
  if (!sorting) {
    return initialSorting;
  }
  return JSON.parse(sorting);
}

/**
 * Generic hook for table page logic - handles search debouncing and table state
 * Eliminates duplicate code between payments, orders, and stores pages
 * Syncs search query with URL parameters
 */

export function useTablePage({
  initialSorting,
  filterMapping,
  queryParamsMapping,
  sortMapping,
}: UseTablePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize search from URL
  const [searchQuery, setSearchQueryState] = React.useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = React.useState(() => searchParams.get('search') || '');

  // Table state managed by table components (useDataTable handles URL sync)
  const [tableState, setTableState] = React.useState<MainTableState>({
    pagination: { pageIndex: getDefaultPageIndex(searchParams), pageSize: getDefaultPageSize(searchParams) },
    sorting: getDefaultSorting(searchParams, initialSorting),
    columnFilters: getDefaultFilter(searchParams, queryParamsMapping)     // columnFilters: []
  });

  // Sync search query to URL
  const setSearchQuery = React.useCallback(
    (value: string) => {
      setSearchQueryState(value);

      // Update URL
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (value) {
          newParams.set('search', value);
        } else {
          newParams.delete('search');
        }
        return newParams;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync search query from URL changes (e.g., back/forward navigation)
  React.useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch !== searchQuery) {
      setSearchQueryState(urlSearch);
    }
  }, [searchParams]);

  // Convert table state to tRPC query input
  const queryInput = React.useMemo(() => ({
    search: debouncedSearch || undefined,
    ...(transformTableStateToTRPCInput(
      tableState.pagination,
      tableState.sorting,
      tableState.columnFilters,
      filterMapping,
      sortMapping,
    ) as any),
  }), [tableState, debouncedSearch, filterMapping, sortMapping]);
  return {
    searchQuery,
    setSearchQuery,
    debouncedSearch,
    tableState,
    setTableState,
    queryInput,
  };
}
